// src/App.js
import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import { supabase } from "./lib/supabaseClient";
import Login from "./Login";
import Leads from "./Leads";
import LeadDetail from "./pages/LeadDetail";
import Discovery from "./pages/Discovery";
import ContentDrafts from "./pages/ContentDrafts";
import FollowUps from "./pages/FollowUps";
import Opportunities from "./pages/Opportunities";
import ContentSources from "./pages/ContentSources";
import Activity from "./pages/Activity";
import Relationships from "./pages/Relationships";
import LiveView from "./pages/LiveView";

// Small badge component
function Badge({ value }) {
  const style = {
    display: "inline-block",
    minWidth: 18,
    padding: "0 6px",
    borderRadius: 999,
    fontSize: 12,
    lineHeight: "18px",
    textAlign: "center",
    border: "1px solid #ddd",
    background: "#f1f5f9",
    marginLeft: 6,
  };
  return <span style={style}>{value}</span>;
}

// Wrap nav so it can use location highlighting
function Nav({ session, discoveryCount, followupCount, opportunityCount }) {
  const loc = useLocation();
  const linkStyle = (path) => ({
    textDecoration: "none",
    padding: "6px 8px",
    borderRadius: 8,
    background: loc.pathname === path ? "#eaeaea" : "transparent",
  });

  if (!session) return null;

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        padding: "10px 14px",
        borderBottom: "1px solid #eee",
        background: "#fafafa",
        flexWrap: "wrap",
      }}
    >
      <strong style={{ marginRight: 8 }}>Claire</strong>
      <Link to="/leads" style={linkStyle("/leads")}>Leads</Link>
      <Link to="/follow-ups" style={linkStyle("/follow-ups")}>
        Follow-ups <Badge value={followupCount} />
      </Link>
      <Link to="/content-drafts" style={linkStyle("/content-drafts")}>Content Drafts</Link>
      <Link to="/opportunities" style={linkStyle("/opportunities")}>
        Opportunities <Badge value={opportunityCount} />
      </Link>
      <Link to="/discovery" style={linkStyle("/discovery")}>
        Discovery <Badge value={discoveryCount} />
      </Link>
      <Link to="/content-sources" style={linkStyle("/content-sources")}>Sources</Link>
      <Link to="/activity" style={linkStyle("/activity")}>Activity</Link>
      <Link to="/relationships" style={linkStyle("/relationships")}>Relationships</Link>
      <Link to="/live" style={{
        ...linkStyle("/live"),
        ...(loc.pathname === "/live" ? {} : { color: "#dc2626" }),
        display: "flex",
        alignItems: "center",
        gap: 4,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%",
          background: "#dc2626",
          display: "inline-block",
          animation: "navPulse 2s infinite",
        }} />
        Live
      </Link>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [discoveryCount, setDiscoveryCount] = useState(0);
  const [followupCount, setFollowupCount] = useState(0);
  const [opportunityCount, setOpportunityCount] = useState(0);

  // ---- Auth bootstrap ----
  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      setSession(session);
      setLoadingSession(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return;
        setSession(session);
      }
    );

    return () => {
      isMounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  // ---- Count fetcher ----
  const fetchDiscoveryCount = useCallback(async () => {
    const { count, error } = await supabase
      .from("leads_discovery")
      .select("*", { count: "exact", head: true });
    if (!error && typeof count === "number") {
      setDiscoveryCount(count);
    }
  }, []);

  // ---- Realtime + instant local bumps via CustomEvent ----
  useEffect(() => {
    if (!session) return;
    let channel;

    // Initial fetch
    fetchDiscoveryCount();

    // Realtime subscription to keep badge live
    channel = supabase
      .channel("ld-count")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leads_discovery" },
        () => fetchDiscoveryCount()
      )
      .subscribe();

    // Listen for local UI hints to bump immediately
    const handler = (e) => {
      const delta = e?.detail?.delta ?? 0;
      if (delta !== 0) {
        setDiscoveryCount((prev) => Math.max(0, prev + delta));
      }
      // Also reconcile with server
      fetchDiscoveryCount();
    };
    window.addEventListener("discovery-count-dirty", handler);

    return () => {
      if (channel) supabase.removeChannel(channel);
      window.removeEventListener("discovery-count-dirty", handler);
    };
  }, [session, fetchDiscoveryCount]);

  // ---- Follow-up badge: sum of both trigger types (reactive + cadence).
  // Recounts whenever an interaction lands or a lead row changes (cadence
  // due-dates live on leads.next_touch_due_at) ----
  const fetchFollowupCount = useCallback(async () => {
    const [reactive, cadence] = await Promise.all([
      supabase.from("v_leads_needing_followup").select("*", { count: "exact", head: true }),
      supabase.from("v_leads_due_for_touch").select("*", { count: "exact", head: true }),
    ]);
    if (!reactive.error && !cadence.error) {
      setFollowupCount((reactive.count || 0) + (cadence.count || 0));
    }
  }, []);

  useEffect(() => {
    if (!session) return;
    fetchFollowupCount();

    const channel = supabase
      .channel("followup-count")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "interactions" },
        () => fetchFollowupCount()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leads" },
        () => fetchFollowupCount()
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [session, fetchFollowupCount]);

  // ---- Opportunities badge: open items (needs_input or ready) ----
  const fetchOpportunityCount = useCallback(async () => {
    const { count, error } = await supabase
      .from("opportunities")
      .select("*", { count: "exact", head: true })
      .in("status", ["needs_input", "ready"]);
    if (!error && typeof count === "number") {
      setOpportunityCount(count);
    }
  }, []);

  useEffect(() => {
    if (!session) return;
    fetchOpportunityCount();

    const channel = supabase
      .channel("opportunity-count")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "opportunities" },
        () => fetchOpportunityCount()
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [session, fetchOpportunityCount]);

  if (loadingSession) return null;

  return (
    <Router>
      <Nav session={session} discoveryCount={discoveryCount} followupCount={followupCount} opportunityCount={opportunityCount} />

      <Routes>
        <Route
          path="/"
          element={!session ? <Login /> : <Navigate to="/leads" replace />}
        />
        <Route
          path="/leads"
          element={session ? <Leads /> : <Navigate to="/" replace />}
        />
        <Route
          path="/leads/:id"
          element={session ? <LeadDetail /> : <Navigate to="/" replace />}
        />
        <Route
          path="/follow-ups"
          element={session ? <FollowUps /> : <Navigate to="/" replace />}
        />
        <Route
          path="/discovery"
          element={session ? <Discovery /> : <Navigate to="/" replace />}
        />
        <Route
          path="/content-drafts"
          element={session ? <ContentDrafts /> : <Navigate to="/" replace />}
        />
        <Route
          path="/opportunities"
          element={session ? <Opportunities /> : <Navigate to="/" replace />}
        />
        <Route
          path="/content-sources"
          element={session ? <ContentSources /> : <Navigate to="/" replace />}
        />
        <Route
          path="/activity"
          element={session ? <Activity /> : <Navigate to="/" replace />}
        />
        <Route
          path="/relationships"
          element={session ? <Relationships /> : <Navigate to="/" replace />}
        />
        <Route
          path="/live"
          element={session ? <LiveView /> : <Navigate to="/" replace />}
        />

        <Route
          path="*"
          element={
            session ? <Navigate to="/leads" replace /> : <Navigate to="/" replace />
          }
        />
      </Routes>
    </Router>
  );
}
